import { CompanyWithPackages, VehicleSurveyImage } from "../interfaces/altair";
import { apiFetch } from "../utils/api";

export interface CalculatePremiumArgs {
    manufactorYear: number;
    vehiclevalue: number;
    isElectricCar: boolean;
}

/**
 * Calculates premium using the backend API
 * Uses centralized API base URL from environment variables
 */
export const calculatePremium = async (
    args: CalculatePremiumArgs
): Promise<CompanyWithPackages[]> => {
    const response = await apiFetch("/Policy/calculate-premium", {
        method: "POST",
        body: JSON.stringify({
            manufactorYear: args.manufactorYear,
            vehiclevalue: args.vehiclevalue,
            isElectricCar: args.isElectricCar,
        }),
    });

    if (!response.ok) {
        let errorMessage = `Backend error while calculating premium: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch {
            // Use default error message if JSON parsing fails
        }
        throw new Error(errorMessage);
    }

    return (await response.json()) as CompanyWithPackages[];
};

export interface SubmitPolicyResponse {
    requestId: string;
    urlDraft: string;
}

/**
 * Submits a policy request to the backend API
 * Uses centralized API base URL from environment variables
 */
export const submitPolicyRequest = async (): Promise<SubmitPolicyResponse> => {
    const response = await apiFetch("/Policy/submit-request", {
        method: "POST",
    });

    if (!response.ok) {
        let errorMessage = `Backend error while submitting policy request: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch {
            // Use default error message if JSON parsing fails
        }
        throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
        requestId: data.requestId || data.RequestId,
        urlDraft: data.urlDraft || data.UrlDraft,
    };
};

export interface SubmitSurveyResponse {
    surveyId: string;
    message: string;
}

export interface SubmitSurveyArgs {
    images: VehicleSurveyImage[];
    damageDescription: string;
}

/**
 * Submits the vehicle survey to the backend API
 * Includes captured vehicle images and AI-generated damage description
 * Images are captured from canvas as JPEG, so we add the data URL prefix
 */
export const submitSurveyRequest = async (
    args: SubmitSurveyArgs
): Promise<SubmitSurveyResponse> => {
    const response = await apiFetch("/Policy/submit-survey", {
        method: "POST",
        body: JSON.stringify({
            Photos: args.images.map((img) => ({
                DocumentName: img.side + ".jpeg",
                // Add data URL prefix for canvas-captured JPEG images
                // Canvas images are captured as JPEG and stored without prefix
                Data: img.data.startsWith("data:")
                    ? img.data
                    : `data:image/jpeg;base64,${img.data}`,
            })),
            Description: args.damageDescription,
        }),
    });

    if (!response.ok) {
        let errorMessage = `Backend error while submitting survey: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch {
            // Use default error message if JSON parsing fails
        }
        throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
        surveyId: data.surveyId || data.SurveyId,
        message: data.message || "Survey submitted successfully.",
    };
};

/**
 * Confirms a previously created policy request
 * Expects the same RequestId that was returned from submit_policy_request
 * Reads paymentAmount from session storage if available
 */
export const confirmPolicyRequest = async (
    requestId: string
): Promise<string> => {
    // Read paymentAmount from session storage
    let paymentAmount: number | null = null;
    try {
        const storedPrice = sessionStorage.getItem("selected_package_price");
        if (storedPrice) {
            paymentAmount = parseFloat(storedPrice);
            console.log(`✅ [PolicyService] Read paymentAmount ${paymentAmount} from session storage`);
        }
    } catch (error) {
        console.error("❌ [PolicyService] Failed to read paymentAmount from session storage:", error);
    }

    const requestBody: { requestId: string; paymentAmount?: number } = { requestId };
    if (paymentAmount !== null && !isNaN(paymentAmount)) {
        requestBody.paymentAmount = paymentAmount;
    }

    const response = await apiFetch("/FawryIntegration/GenerateFawryPayLink", {
        method: "POST",
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
        let errorMessage = `Backend error while confirming policy request: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            errorMessage = (errorData && (errorData.message || errorData.error)) || errorMessage;
        } catch {
            // Use default error message if JSON parsing fails
        }
        throw new Error(errorMessage);
    }

    // Try to parse JSON first (expected to include payment link)
    try {
        const data = await response.json();
        const link =
            data.payLink ||
            data.paymentLink ||
            data.url ||
            data.link ||
            data.PayLink ||
            data.PaymentLink ||
            data.Url ||
            data.Link;
        if (link && typeof link === "string") {
            return link;
        }
        // Fallback to stringify payload if link not found
        return JSON.stringify(data);
    } catch {
        // If not JSON, fallback to plain text
        const text = await response.text();
        return text || "تم إنشاء رابط الدفع بنجاح";
    }
};
